import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Send,
  MapPin,
  Mail,
  Phone,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  "Web Development",
  "Mobile App Development",
  "Custom Software",
  "Consulting",
  "Other",
];

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);

      // Track Google Ads conversion
      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-18139644323/eCLkCLzuraccEKOD1MlD",
          value: 1.0,
          currency: "INR",
        });
      }

      setSubmitted(true);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#0a0f1e]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]"
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
              Get In Touch
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white mb-6">
              Let's Build Something{" "}
              <span className="gradient-text-blue">Extraordinary</span>
            </h2>
            <p className="text-base text-slate-400 leading-relaxed mb-10">
              Have a project in mind? We'd love to hear about it. Reach out and
              let's explore how Softlume can help bring your vision to life.
            </p>

            <div className="space-y-6 mb-8">
              {[
                { icon: Mail, title: "Email Us", detail: "info@softlume.in" },
                { icon: Phone, title: "Call Us", detail: "+91 7827782844" },
                {
                  icon: MapPin,
                  title: "Visit Us",
                  detail: "Ghitorni, New Delhi, India 110030",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20 transition-colors">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {item.title}
                    </div>
                    <div className="text-sm text-slate-400">{item.detail}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button
              data-testid="contact-explore-more"
              onClick={() => navigate("/contact")}
              className="bg-transparent border border-white/10 text-slate-300 hover:text-white hover:border-blue-500/40 hover:bg-blue-500/5 rounded-full px-6 h-10 text-sm font-medium transition-all duration-300 group"
            >
              Visit Full Contact Page
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40, rotateY: 5 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
            transition={{
              duration: 0.7,
              delay: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="p-6 md:p-8 rounded-2xl border border-white/5 bg-slate-900/30 backdrop-blur-md hover:border-white/10 transition-colors duration-500"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-300 text-sm">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300 text-sm">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-slate-300 text-sm">
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20 h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service" className="text-slate-300 text-sm">
                    Service Interested In
                  </Label>
                  <select
                    id="service"
                    name="service"
                    data-testid="contact-service-select"
                    value={form.service}
                    onChange={handleChange}
                    className="flex h-11 w-full rounded-md border border-white/10 bg-slate-800/50 px-3 py-2 text-sm text-white focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 focus:outline-none"
                  >
                    <option value="" className="bg-slate-900 text-slate-400">
                      Select a service
                    </option>
                    {serviceOptions.map((s) => (
                      <option
                        key={s}
                        value={s}
                        className="bg-slate-900 text-white"
                      >
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2 mb-6">
                <Label htmlFor="message" className="text-slate-300 text-sm">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-textarea"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows={5}
                  className="bg-slate-800/50 border-white/10 text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:ring-blue-500/20 resize-none"
                />
              </div>
              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl h-12 text-base font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : submitted ? (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Message Sent!
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
